import { useEffect, useRef } from 'react';
import { FullProject, RecurringTaskConfig, ProjectMetadata, Task } from '../types';

export function useRecurringTasksScheduler(
  allProjects: FullProject[],
  addTask: (type: Task['type'], parentId: string | number | undefined, projectId: string, name: string, initialData: Partial<Task>) => Task | undefined | void,
  updateProjectMetadata: (projectId: string, updates: Partial<ProjectMetadata>) => void
) {
  const allProjectsRef = useRef(allProjects);
  allProjectsRef.current = allProjects;

  const addTaskRef = useRef(addTask);
  addTaskRef.current = addTask;

  const updateProjectMetadataRef = useRef(updateProjectMetadata);
  updateProjectMetadataRef.current = updateProjectMetadata;

  useEffect(() => {
    const checkRecurringTasks = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const currentDate = now.getDate();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const nowStr = `${y}-${m}-${d}`;

      const inboxProject = allProjectsRef.current.find(p => p.id === 'inbox_inforum');
      const vacations = inboxProject?.metadata?.vacations || [];
      const isVacationModeActive = inboxProject?.metadata?.vacationModeActive === true;
      const isOnVacation = isVacationModeActive || vacations.some(v => nowStr >= v.start && nowStr <= v.end);

      allProjectsRef.current.forEach(project => {
        // Skip global pseudo projects
        if (['dashboard', 'metricsDashboard', 'globalKanban', 'communication', 'impactEffort', 'noDateTasks', 'global', 'globalCalendar', 'aiAssistant', 'analysis'].includes(project.id)) {
          return;
        }

        const rules = project.metadata?.recurringTasks || [];
        let metadataChanged = false;

        const updatedRules = rules.map(rule => {
          if (rule.isPaused) return rule;

          const [ruleHourStr, ruleMinuteStr] = rule.time.split(':');
          const ruleHour = parseInt(ruleHourStr, 10);
          const ruleMinute = parseInt(ruleMinuteStr, 10);

          if (isNaN(ruleHour) || isNaN(ruleMinute)) return rule;

          const timePassedToday = 
            currentHour > ruleHour || 
            (currentHour === ruleHour && currentMinute >= ruleMinute);

          let isDue = false;
          const lastRunDate = rule.lastRun ? new Date(rule.lastRun) : null;

          if (rule.frequency === 'daily') {
            const hasRunToday = lastRunDate && 
              lastRunDate.getFullYear() === currentYear &&
              lastRunDate.getMonth() === currentMonth &&
              lastRunDate.getDate() === currentDate;

            if (!hasRunToday && timePassedToday) {
              isDue = true;
            }
          } 
          else if (rule.frequency === 'weekly') {
            const isTargetDay = rule.weekdays?.includes(currentDay);
            const hasRunToday = lastRunDate && 
              lastRunDate.getFullYear() === currentYear &&
              lastRunDate.getMonth() === currentMonth &&
              lastRunDate.getDate() === currentDate;

            if (isTargetDay && !hasRunToday && timePassedToday) {
              isDue = true;
            }
          } 
          else if (rule.frequency === 'monthly') {
            let targetDate = rule.dayOfMonth !== undefined ? rule.dayOfMonth : new Date(rule.createdAt).getDate();

            const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
            if (targetDate > daysInMonth) {
              targetDate = daysInMonth;
            }

            const isTargetDay = currentDate === targetDate;
            const hasRunThisMonth = lastRunDate && 
              lastRunDate.getFullYear() === currentYear &&
              lastRunDate.getMonth() === currentMonth;

            if (isTargetDay && !hasRunThisMonth && timePassedToday) {
              isDue = true;
            }
          }

          if (isDue) {
            metadataChanged = true;

            if (!isOnVacation) {
              const isDateBucket = ['overdue', 'today', 'week', 'month', 'longterm', 'completed'].includes(rule.columnId);
              
              const projectColumns = project.metadata?.customColumns || [];
              const defaultStatus = projectColumns.length > 0 ? projectColumns[0].id : 'todo';
              const completedStatus = projectColumns.find(c => c.category === 'completed')?.id || 'completed';
              
              let taskStatus = rule.columnId;
              let taskStart: string | undefined = undefined;
              let taskEnd: string | undefined = undefined;
              let taskCompleted = false;

              if (isDateBucket) {
                taskStatus = rule.columnId === 'completed' ? completedStatus : defaultStatus;
                taskCompleted = rule.columnId === 'completed';
                
                if (rule.columnId === 'today' || rule.columnId === 'completed') {
                  taskStart = nowStr;
                  taskEnd = nowStr;
                } else if (rule.columnId === 'week') {
                  taskStart = nowStr;
                  const fut = new Date(now);
                  fut.setDate(fut.getDate() + 3);
                  const fy = fut.getFullYear();
                  const fm = String(fut.getMonth() + 1).padStart(2, '0');
                  const fd = String(fut.getDate()).padStart(2, '0');
                  taskEnd = `${fy}-${fm}-${fd}`;
                } else if (rule.columnId === 'month') {
                  taskStart = nowStr;
                  const fut = new Date(now);
                  fut.setDate(fut.getDate() + 14);
                  const fy = fut.getFullYear();
                  const fm = String(fut.getMonth() + 1).padStart(2, '0');
                  const fd = String(fut.getDate()).padStart(2, '0');
                  taskEnd = `${fy}-${fm}-${fd}`;
                } else if (rule.columnId === 'overdue') {
                  const past = new Date(now);
                  past.setDate(past.getDate() - 1);
                  const py = past.getFullYear();
                  const pm = String(past.getMonth() + 1).padStart(2, '0');
                  const pd = String(past.getDate()).padStart(2, '0');
                  taskStart = `${py}-${pm}-${pd}`;
                  taskEnd = `${py}-${pm}-${pd}`;
                }
              }

              addTaskRef.current(
                'task',
                undefined,
                project.id,
                rule.title,
                {
                  status: taskStatus,
                  start: taskStart,
                  end: taskEnd,
                  description: rule.description,
                  priority: rule.priority || 'none',
                  assignee: rule.assignee,
                  type: (rule.type as Task['type']) || 'task',
                  completed: taskCompleted,
                  isInlineNew: false
                }
              );
            }

            return {
              ...rule,
              lastRun: now.toISOString()
            };
          }

          return rule;
        });

        if (metadataChanged) {
          updateProjectMetadataRef.current(project.id, {
            recurringTasks: updatedRules
          });
        }
      });
    };

    // Run 5 seconds after boot
    const initialTimeout = setTimeout(checkRecurringTasks, 5000);

    // Run every 60 seconds
    const interval = setInterval(checkRecurringTasks, 60000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
}
